import clsx from 'clsx';
import React, { useId, useState } from 'react';
import { HueGlyph } from './Feedback';
import { Collapse } from './Journey';
import { Surface } from './Surface';
import { type Hue, HUE_WASH } from './hue';
import { IconCheck, IconChevronRight, IconPhone } from './icons';

/**
 * Checkout kit: the pieces an offer page is built from — numbered step
 * cards on the canvas, the selected plan as one calm tile, and the
 * receipt's supporting blocks (`ReceiptGroup`/`ReceiptDue` live in
 * Receipt.tsx). Reference: `src/pages/Public/ActivationPage.tsx`.
 */

/**
 * One numbered section of a checkout, as a `Surface` card: numeral · title
 * · optional one-line description, content indented to the title. The card
 * never owns a submit; the Receipt beside it does.
 */
export const StepCard = React.forwardRef<
  HTMLElement,
  {
    n: number;
    title: React.ReactNode;
    description?: React.ReactNode;
    /** Section-level validation error, announced under the title. */
    error?: string;
    id?: string;
    children: React.ReactNode;
    className?: string;
  }
>(({ n, title, description, error, id, children, className }, ref) => {
  const headingId = useId();
  return (
    <Surface
      as="section"
      ref={ref}
      id={id}
      aria-labelledby={headingId}
      className={clsx('p-5 sm:p-6', className)}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="v2-num mt-px inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-v2-fg text-v2-12 font-semibold text-v2-surface"
        >
          {n}
        </span>
        <div className="min-w-0 flex-1">
          <h2
            id={headingId}
            className="text-v2-16 font-semibold leading-6 tracking-[-0.01em] text-v2-fg"
          >
            {title}
          </h2>
          {description && (
            <p className="mt-0.5 max-w-[68ch] text-v2-12 leading-[18px] text-v2-fg-3">
              {description}
            </p>
          )}
          {error && (
            <p role="alert" className="mt-1 text-v2-12 text-v2-danger">
              {error}
            </p>
          )}
        </div>
      </div>
      <div className="mt-4 sm:pl-9">{children}</div>
    </Surface>
  );
});
StepCard.displayName = 'StepCard';

/**
 * The plan someone is signing up to, as one tile on the plan's hue wash:
 * glyph · name (and a quiet tag) · the one meta line · price. `children`
 * (the feature checklist) sit under a hairline inside the tile. For a
 * *choice* of plans use `PlanChooser`; this is the presentation of the one
 * already chosen.
 */
export const PlanCard: React.FC<{
  name: React.ReactNode;
  hue: Hue;
  /** Icon in the tile; defaults to the phone. */
  glyph?: React.ReactNode;
  /** A `Tag` beside the name ("Your pricing"). */
  tag?: React.ReactNode;
  /** "400 min included · $0.36/min after". */
  meta?: React.ReactNode;
  price: React.ReactNode;
  priceSub?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}> = ({
  name,
  hue,
  glyph,
  tag,
  meta,
  price,
  priceSub,
  children,
  className,
}) => (
  <div className={clsx('rounded-v2-lg p-4', HUE_WASH[hue], className)}>
    <div className="flex items-center gap-3">
      <HueGlyph hue={hue} size="lg">
        {glyph ?? <IconPhone size={18} />}
      </HueGlyph>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-v2-15 font-semibold tracking-[-0.01em] text-v2-fg">
            {name}
          </span>
          {tag}
        </div>
        {meta && (
          <p className="v2-num mt-0.5 text-v2-12 text-v2-fg-3">{meta}</p>
        )}
      </div>
      <div className="v2-num shrink-0 text-right">
        <span className="text-v2-18 font-bold tracking-[-0.01em] text-v2-fg">
          {price}
        </span>
        {priceSub && (
          <span className="text-v2-12 text-v2-fg-3">{priceSub}</span>
        )}
      </div>
    </div>
    {children && (
      <div className="mt-3.5 border-t border-v2-line/70 pt-3.5">{children}</div>
    )}
  </div>
);

/** Two-column checklist of what a plan comes with. */
export const FeatureChecks: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <ul className={clsx('grid gap-x-6 gap-y-2 sm:grid-cols-2', className)}>
    {children}
  </ul>
);

const CheckGlyph: React.FC<{ on: boolean; className?: string }> = ({
  on,
  className,
}) => (
  <span
    aria-hidden
    className={clsx(
      'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
      on
        ? 'bg-v2-accent text-v2-on-accent'
        : 'border border-v2-line-strong bg-v2-surface',
      className,
    )}
  >
    {on && <IconCheck size={10} />}
  </span>
);

/**
 * One line of the checklist. Without `onChange` it is a fact of the plan (a
 * filled check and a label). With `onChange` it is a real checkbox drawn as
 * the same check, for an add-on that can be switched here; `note` carries
 * its price ("+$49/mo") or "Included".
 */
export const FeatureCheck: React.FC<{
  label: React.ReactNode;
  /** Quieter second line (what the add-on does). */
  sub?: React.ReactNode;
  /** Trailing quiet note: a price or "Included". */
  note?: React.ReactNode;
  checked?: boolean;
  onChange?: (on: boolean) => void;
  disabledReason?: string;
}> = ({ label, sub, note, checked, onChange, disabledReason }) => {
  const body = (
    <>
      <span className="min-w-0 flex-1">
        <span className="block text-v2-13 leading-5 text-v2-fg-2">{label}</span>
        {sub && (
          <span className="block text-v2-12 leading-[18px] text-v2-fg-3">
            {sub}
          </span>
        )}
      </span>
      {note && (
        <span className="v2-num shrink-0 pt-0.5 text-v2-12 text-v2-fg-3">
          {note}
        </span>
      )}
    </>
  );
  if (!onChange)
    return (
      <li className="flex items-start gap-2">
        <CheckGlyph on={checked ?? true} />
        {body}
      </li>
    );
  return (
    <li>
      <label
        className={clsx(
          'flex items-start gap-2',
          disabledReason ? 'cursor-default' : 'cursor-pointer',
        )}
        title={disabledReason}
      >
        <input
          type="checkbox"
          checked={!!checked}
          disabled={!!disabledReason}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <CheckGlyph
          on={!!checked}
          className="peer-focus-visible:ring-2 peer-focus-visible:ring-v2-accent/40 peer-focus-visible:ring-offset-1"
        />
        {body}
      </label>
    </li>
  );
};

/**
 * A quiet disclosure for what most people don't need to read: the rest of
 * the add-ons, the full terms summary. Content stays mounted; while closed
 * it is out of the accessibility tree and the tab order.
 */
export const Reveal: React.FC<{
  label: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
}> = ({ label, defaultOpen, children, className }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const id = useId();
  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-v2-sm text-v2-12 font-medium text-v2-fg-2 hover:text-v2-fg"
      >
        <IconChevronRight
          size={12}
          className={clsx('transition-transform', open && 'rotate-90')}
        />
        {label}
      </button>
      <Collapse open={open} id={id} className="mt-2">
        {children}
      </Collapse>
    </div>
  );
};

/** Row of key facts, each an icon tile with a line and a quiet sub-line. */
export const IconFacts: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div className={clsx('flex flex-wrap gap-x-8 gap-y-3', className)}>
    {children}
  </div>
);

export const IconFact: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
  sub?: React.ReactNode;
}> = ({ icon, children, sub }) => (
  <div className="flex items-center gap-2.5">
    <span
      aria-hidden
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-v2 bg-v2-sunken text-v2-fg-2 ring-1 ring-inset ring-v2-line"
    >
      {icon}
    </span>
    <span className="min-w-0">
      <span className="block text-v2-13 font-medium leading-5 text-v2-fg">
        {children}
      </span>
      {sub && (
        <span className="block text-v2-12 leading-4 text-v2-fg-3">{sub}</span>
      )}
    </span>
  </div>
);

/** "What happens next?": the few steps after the primary, numbered quietly. */
export const NextSteps: React.FC<{
  title?: React.ReactNode;
  items: React.ReactNode[];
  className?: string;
}> = ({ title, items, className }) => (
  <div className={className}>
    {title && <h3 className="text-v2-13 font-semibold text-v2-fg">{title}</h3>}
    <ol className={clsx('flex flex-col gap-2.5', title && 'mt-2.5')}>
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-start gap-2.5 text-v2-12 leading-[18px] text-v2-fg-2"
        >
          <span
            aria-hidden
            className="v2-num inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-v2-sunken text-v2-11 font-semibold text-v2-fg-2 ring-1 ring-inset ring-v2-line"
          >
            {i + 1}
          </span>
          <span className="min-w-0 pt-px">{item}</span>
        </li>
      ))}
    </ol>
  </div>
);
